import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Slider from '@material-ui/core/Slider'

const PANEL_WIDTH = 55
const TRACK_WIDTH = 5
const TRACK_GUTTER = 4
const BODY_HEIGHT = 320
const LABELS_WIDTH = 22

const usePanelStyles = makeStyles((theme) => ({
  panel: {
    width: PANEL_WIDTH,
    backgroundColor: 'rgba(255,255,255,0.92)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    border: '1.5px solid rgba(0,0,0,0.1)',
    borderRadius: 12,
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
    padding: theme.spacing(0.75),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 6,
    fontFamily:
      'Roboto, "Fira Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    pointerEvents: 'auto',
  },
  headerValue: {
    fontSize: '0.78rem',
    fontWeight: 700,
    color: '#232C57',
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.1,
  },
  body: {
    height: BODY_HEIGHT,
    display: 'flex',
    alignItems: 'stretch',
    paddingBottom: 10,
    boxSizing: 'border-box',
  },
  sliderColumn: {
    width: TRACK_WIDTH + TRACK_GUTTER * 2,
    height: '100%',
    flexShrink: 0,
  },
  labelsColumn: {
    width: LABELS_WIDTH,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  },
  label: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: 9,
    fontWeight: 600,
    color: 'rgba(0,0,0,0.65)',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap',
    transition: 'opacity 0.2s ease, color 0.2s ease',
    paddingLeft: 2,
  },
  labelDimmed: {
    opacity: 0.4,
  },
}))

const useSliderStyles = makeStyles({
  root: {
    width: TRACK_WIDTH,
    height: '100%',
    padding: `0 ${TRACK_GUTTER}px`,
    color: '#232C57',
  },
  rail: ({ gradient }) => ({
    width: 'auto',
    left: TRACK_GUTTER,
    right: TRACK_GUTTER,
    opacity: 1,
    borderRadius: 4,
    background: gradient,
    boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
  }),
  track: {
    width: 'auto',
    left: TRACK_GUTTER,
    right: TRACK_GUTTER,
    background: 'rgba(0,0,0,0.42)',
    opacity: 1,
    borderRadius: 0,
  },
  thumb: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: '#232C57',
    border: '1.5px solid #fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.3), 0 0 0 1px rgba(0,0,0,0.08)',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
    zIndex: 4,
    // Beat MUI's `.MuiSlider-vertical .MuiSlider-thumb` (specificity 0,2,0)
    // which would otherwise force marginLeft: -5 / marginBottom: -6.
    marginLeft: '-13px !important',
    marginBottom: '-5px !important',
    marginTop: 'auto',
    '&:focus, &:hover, &$active': {
      transform: 'scale(1.1)',
      boxShadow: '0 3px 10px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.12)',
    },
  },
  active: {},
  valueLabel: {
    left: 'calc(100% + 6px)',
    right: 'auto',
    top: '50%',
    transform: 'translate(0, -50%) !important',
    zIndex: 5,
    '& > span': {
      width: 'auto',
      height: 'auto',
      transform: 'none',
      borderRadius: 0,
      backgroundColor: 'transparent',
      display: 'block',
    },
    '& > span > span': {
      position: 'relative',
      display: 'inline-block',
      background: '#232C57',
      color: '#fff',
      fontSize: '0.5rem',
      fontWeight: 700,
      lineHeight: 1,
      letterSpacing: '0.01em',
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
      padding: '1px 3px',
      borderRadius: 2,
      boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
      transform: 'none',
      '&::before': {
        content: '""',
        position: 'absolute',
        left: -2,
        top: '50%',
        width: 0,
        height: 0,
        transform: 'translateY(-50%)',
        borderTop: '3px solid transparent',
        borderBottom: '3px solid transparent',
        borderRight: '3px solid #232C57',
      },
    },
  },
})

export default function LegendFilter({ legend, value, onChange }) {
  const classes = usePanelStyles()

  if (!legend?.length) return null

  const min = parseFloat(legend.at(0).label)
  const max = parseFloat(legend.at(-1).label)
  const step = ((max - min) / legend.length) / 10 || 0.01

  const reversed = legend.slice(0).reverse()

  // Hard-stop gradient for the slider's rail — bottom (low) → top (high)
  const stops = legend.flatMap((entry, i) => {
    const lo = ((i / legend.length) * 100).toFixed(2)
    const hi = (((i + 1) / legend.length) * 100).toFixed(2)
    return [`${entry.color} ${lo}%`, `${entry.color} ${hi}%`]
  })
  const gradient = `linear-gradient(to top, ${stops.join(', ')})`

  const sliderClasses = useSliderStyles({ gradient })

  const currentValue = typeof value === 'number' ? value : min
  const [thumbValue, setThumbValue] = React.useState(currentValue)

  React.useEffect(() => {
    setThumbValue(currentValue)
  }, [currentValue])

  return (
    <div className={classes.panel}>
      <span className={classes.headerValue}>{currentValue.toFixed(2)}</span>

      <div className={classes.body}>
        <div className={classes.sliderColumn}>
          <Slider
            key={`legend-filter-${min}-${max}`}
            classes={sliderClasses}
            orientation="vertical"
            min={min}
            max={max}
            step={step}
            value={thumbValue}
            valueLabelDisplay="on"
            valueLabelFormat={(x) => x.toFixed(2)}
            onChange={(_, v) => setThumbValue(v)}
            onChangeCommitted={(_, v) => onChange(v)}
          />
        </div>

        <div className={classes.labelsColumn}>
          {reversed.map((entry, i) => {
            const segLabel = parseFloat(entry.label)
            const dimmed = segLabel < currentValue
            return (
              <div
                key={`${entry.color}-${i}`}
                className={`${classes.label} ${
                  dimmed ? classes.labelDimmed : ''
                }`}
              >
                {entry.label}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
